import { createMemo, createSignal, type Accessor } from 'solid-js';

/**
 * A Solid signal packaged as one callable: `s()` reads, `s.set(v)` writes,
 * `s.update(fn)` derives the next value. Services and stores keep their state
 * in these so the read/write surface stays on one name.
 *
 * Solid 2 batches writes: after `s.set(v)` a read in the SAME synchronous flow
 * still returns the previous value until the microtask flush. When logic needs
 * the value it just wrote, keep it in a local variable.
 */
export interface Signal<T> extends Accessor<T> {
  set(value: T): void;
  update(fn: (prev: T) => T): void;
  asReadonly(): Accessor<T>;
}

export function signal<T>(initial: T, options?: { equals?: false | ((a: T, b: T) => boolean) }): Signal<T> {
  // Owned writes are allowed: stores are created at module scope but written
  // from effects and lifecycles across the app.
  const [read, write] = createSignal<T>(initial as Exclude<T, Function>, { ...options, ownedWrite: true } as never);
  const s = (() => read()) as Signal<T>;
  s.set = (value: T) => void write(() => value);
  s.update = (fn: (prev: T) => T) => void write((prev: T) => fn(prev));
  s.asReadonly = () => read;
  return s;
}

/**
 * A derived read for service/store state. Lazy: computed on first read, so
 * module-scope stores can reference each other without import-order hazards.
 */
export function computed<T>(fn: () => T): Accessor<T> {
  return createMemo(fn, { lazy: true });
}
